import { Link } from 'react-router-dom';
import { Building2, Users, ClipboardList, Receipt, Calendar, MessageSquareWarning, CheckCircle2, FileText, Banknote, FileSpreadsheet, FileUp, Inbox, MapPin, Settings, Tags, BookOpen, Newspaper, MessageSquareQuote, Bell, UserCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { formatPrice, formatDate } from '../../lib/format';
import { StatCard, Spinner, EmptyState, StatusBadge } from '../../components/ui';
import QuickLinks from '../../components/QuickLinks';
import DashboardStatus from '../../components/DashboardStatus';
import { useLivePoll } from '../../lib/useLivePoll';

const QUICK_LINKS = [
  { to: '/admin/properties', label: 'Properties', icon: <Building2 className="h-5 w-5" />, description: 'Listings & media' },
  { to: '/admin/staff', label: 'Staff & Users', icon: <Users className="h-5 w-5" />, description: 'Accounts & roles' },
  { to: '/admin/assignments', label: 'Assignments', icon: <ClipboardList className="h-5 w-5" />, description: 'Work allocation' },
  { to: '/admin/reports', label: 'Status Report', icon: <FileText className="h-5 w-5" />, description: 'All submissions' },
  { to: '/admin/complaints', label: 'Complaints', icon: <MessageSquareWarning className="h-5 w-5" />, description: 'Resident issues' },
  { to: '/admin/bills', label: 'Bills', icon: <Banknote className="h-5 w-5" />, description: 'Site bills' },
  { to: '/admin/expenses', label: 'My Expenses', icon: <Receipt className="h-5 w-5" />, description: 'Personal claims' },
  { to: '/admin/monthly-expenses', label: 'Monthly Expenses', icon: <FileSpreadsheet className="h-5 w-5" />, description: 'Site uploads' },
  { to: '/admin/expense-reports', label: 'Site Expenses', icon: <FileUp className="h-5 w-5" />, description: 'Expense reports' },
  { to: '/admin/account-reports', label: 'Financial Account', icon: <FileUp className="h-5 w-5" />, description: 'Account reports' },
  { to: '/admin/requests', label: 'Requests', icon: <Inbox className="h-5 w-5" />, description: 'Staff requests' },
  { to: '/admin/meetings', label: 'Meetings', icon: <Calendar className="h-5 w-5" />, description: 'Schedule & records' },
  { to: '/admin/locations', label: 'States & Areas', icon: <MapPin className="h-5 w-5" />, description: 'Locations' },
  { to: '/admin/features', label: 'Features', icon: <Settings className="h-5 w-5" />, description: 'Property features' },
  { to: '/admin/price-ranges', label: 'Price Ranges', icon: <Tags className="h-5 w-5" />, description: 'Pricing brackets' },
  { to: '/admin/staff-handbook', label: 'Staff Handbook', icon: <BookOpen className="h-5 w-5" />, description: 'Policies & docs' },
  { to: '/admin/blog', label: 'Blog', icon: <Newspaper className="h-5 w-5" />, description: 'Posts & media' },
  { to: '/admin/testimonials', label: 'Testimonials', icon: <MessageSquareQuote className="h-5 w-5" />, description: 'Reviews' },
  { to: '/admin/notices', label: 'Notices', icon: <Bell className="h-5 w-5" />, description: 'Announcements' },
  { to: '/admin/profile', label: 'My Profile', icon: <UserCircle className="h-5 w-5" />, description: 'Account details' },
];

interface AdminDashboardData {
  stats: {
    totalProperties: number;
    soldProperties: number;
    availableProperties: number;
    featuredProperties: number;
    staffCount: number;
    managerCount: number;
    accountCount: number;
    totalUsers: number;
    totalReports: number;
    openAssignments: number;
    pendingExpenses: number;
    pendingExpenseAmount: number;
    totalExpenses: number;
    pendingMonthlyExpenses: number;
    upcomingMeetings: number;
    completedMeetings: number;
    openComplaints: number;
  };
  recentReports: Array<{ id: number; report_type: string; report_date: string; name: string; role: string }>;
  recentComplaints: Array<{ id: number; subject: string; status: string; created_at: string; name: string }>;
  recentExpenses: Array<{ id: number; category: string; amount: number; currency: string; status: string; expense_date: string; user_name: string }>;
  recentMonthlyExpenses: Array<{ id: number; title: string | null; month: string; status: string; created_at: string; user_name: string }>;
  upcomingMeetings: Array<{ id: number; title: string; meeting_date: string; duration: number; meeting_url: string; status: string }>;
}

export default function Dashboard() {
  const { data, loading, refreshing, lastUpdated, reload } = useLivePoll<AdminDashboardData>(
    () => api.get<AdminDashboardData>('/dashboard')
  );

  if (loading) return <Spinner label="Loading dashboard..." />;
  if (!data) return <EmptyState message="Could not load dashboard data." />;

  const s = data.stats;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <DashboardStatus lastUpdated={lastUpdated} refreshing={refreshing} onRefresh={reload} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<Building2 className="h-8 w-8" />}
          label="Total Properties"
          value={s.totalProperties}
          sub={`${s.availableProperties} available · ${s.soldProperties} sold · ${s.featuredProperties} featured`}
        />
        <StatCard
          icon={<Users className="h-8 w-8" />}
          label="Total Users"
          value={s.totalUsers}
          color="text-green-600"
          sub={`${s.managerCount} managers · ${s.staffCount} staff · ${s.accountCount} accounts`}
        />
        <StatCard
          icon={<ClipboardList className="h-8 w-8" />}
          label="Open Assignments"
          value={s.openAssignments}
          color="text-purple-600"
          sub={`${s.totalReports} reports submitted`}
        />
        <StatCard
          icon={<Receipt className="h-8 w-8" />}
          label="Pending Expenses"
          value={s.pendingExpenses}
          color="text-yellow-600"
          sub={formatPrice(s.pendingExpenseAmount, 'NGN')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<Receipt className="h-8 w-8" />}
          label="Total Expenses"
          value={s.totalExpenses}
          sub="all time"
        />
        <StatCard
          icon={<Receipt className="h-8 w-8" />}
          label="Pending Monthly Expenses"
          value={s.pendingMonthlyExpenses}
          color="text-yellow-600"
          sub="uploads awaiting approval"
        />
        <StatCard
          icon={<Calendar className="h-8 w-8" />}
          label="Upcoming Meetings"
          value={s.upcomingMeetings}
          color="text-primary"
        />
        <StatCard
          icon={<CheckCircle2 className="h-8 w-8" />}
          label="Completed Meetings"
          value={s.completedMeetings}
          color="text-green-600"
        />
        <StatCard
          icon={<MessageSquareWarning className="h-8 w-8" />}
          label="Open Complaints"
          value={s.openComplaints}
          color="text-red-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Recent Reports</h2>
          {data.recentReports.length === 0 ? (
            <EmptyState message="No reports yet." />
          ) : (
            <div className="space-y-4">
              {data.recentReports.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">{r.name}</p>
                    <p className="text-sm text-gray-500 capitalize">{r.report_type} report · {formatDate(r.report_date)}</p>
                  </div>
                  <Link to="/admin/reports" className="text-primary text-sm hover:underline">View</Link>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Recent Complaints</h2>
          {data.recentComplaints.length === 0 ? (
            <EmptyState message="No complaints." />
          ) : (
            <div className="space-y-4">
              {data.recentComplaints.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">{c.subject}</p>
                    <p className="text-sm text-gray-500">{c.name} · {formatDate(c.created_at)}</p>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Upcoming Meetings</h2>
          {data.upcomingMeetings.length === 0 ? (
            <EmptyState message="No upcoming meetings." />
          ) : (
            <div className="space-y-4">
              {data.upcomingMeetings.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-primary mr-3 shrink-0" />
                    <div>
                      <p className="font-medium">{m.title}</p>
                      <p className="text-sm text-gray-500">{formatDate(m.meeting_date)} · {m.duration} min</p>
                    </div>
                  </div>
                  <StatusBadge status={m.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Recent Expenses</h2>
          {data.recentExpenses.length === 0 ? (
            <EmptyState message="No expenses yet." />
          ) : (
            <div className="space-y-4">
              {data.recentExpenses.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">{e.category}</p>
                    <p className="text-sm text-gray-500">{e.user_name} · {formatDate(e.expense_date)}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="font-medium">{formatPrice(e.amount, e.currency)}</span>
                    <StatusBadge status={e.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-lg lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Recent Monthly Expenses</h2>
            <Link to="/admin/monthly-expenses" className="text-primary text-sm hover:underline">View all</Link>
          </div>
          {data.recentMonthlyExpenses.length === 0 ? (
            <EmptyState message="No monthly expense uploads yet." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.recentMonthlyExpenses.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 flex-wrap py-2 border-b last:border-b-0">
                  <div>
                    <p className="font-medium">{m.title || `Monthly Expenses ${formatDate(m.month)}`}</p>
                    <p className="text-sm text-gray-500">{m.user_name} · {formatDate(m.month)}</p>
                  </div>
                  <StatusBadge status={m.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <QuickLinks links={QUICK_LINKS} title="All Information" />
      </div>
    </div>
  );
}